import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { AWS_CONFIGURATION } from './config';
import { fetchAuthSession } from 'aws-amplify/auth';

// Import components
import Main from './components/Main.jsx';
import Settings from './components/settings/Settings.jsx';
import DoctorsListWithRouter from './components/settings/DoctorsList.jsx';
import ReportsList from './components/settings/ReportsList.jsx';
import DoctorDetails from './components/settings/DoctorDetails.jsx';
import DoctorSchedule from './components/settings/DoctorSchedule.jsx';
import DoctorAlerts from './components/settings/DoctorAlerts.jsx';
import DoctorFields from './components/settings/DoctorFields.jsx';
import DoctorBilling from './components/settings/DoctorBilling.jsx';
import Report from './components/settings/Report.jsx';
import AppointmentDetails from './components/calendar/AppointmentDetails.jsx';
import PendingAppointments from './components/calendar/PendingAppointments.jsx';

// Add diagnostic authentication check
const checkAndLogAuthentication = async () => {
  try {
    console.log('App.js: Checking authentication status...');
    const authSession = await fetchAuthSession();
    
    if (!authSession?.tokens?.idToken) {
      console.log('App.js: No authentication token available');
      return false;
    }
    
    const token = authSession.tokens.idToken;
    const expiration = new Date(token.payload.exp * 1000);
    const timeUntilExpiration = expiration.getTime() - Date.now();
    const minutesUntilExpiration = Math.floor(timeUntilExpiration / (60 * 1000));
    
    console.log(`App.js: User is authenticated as ${token.payload.email || token.payload['cognito:username']}`);
    console.log(`App.js: Token expires in ${minutesUntilExpiration} minutes (${expiration.toLocaleString()})`);
    
    // Log any custom claims that might affect permissions
    console.log('App.js: Token payload:', JSON.stringify({
      groups: token.payload['cognito:groups'],
      roles: token.payload['custom:role'] || token.payload.role,
      permissions: token.payload.permissions
    }, null, 2));
    
    return true;
  } catch (error) {
    console.error('App.js: Error checking authentication:', error);
    return false;
  }
};

function App() {
  // Run authentication check when the app loads
  useEffect(() => {
    // Configure Amplify
    Amplify.configure(AWS_CONFIGURATION);
    
    // Check authentication status
    checkAndLogAuthentication();
  }, []);
  
  return (
    <Router>
      <Routes>
        {/* Only include routes for components that actually exist */}
        <Route path="/" element={<Main />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/doctors" element={<DoctorsListWithRouter />} />
        <Route path="/settings/doctors/new" element={<DoctorDetails />} />
        <Route path="/settings/doctors/:docId" element={<DoctorDetails />} />
        <Route path="/settings/doctors/:docId/schedule" element={<DoctorSchedule />} />
        <Route path="/settings/doctors/:docId/alerts" element={<DoctorAlerts />} />
        <Route path="/settings/doctors/:docId/fields" element={<DoctorFields />} />
        <Route path="/settings/doctors/:docId/billing" element={<DoctorBilling />} />
        <Route path="/settings/reports" element={<ReportsList />} />
        <Route path="/settings/reports/:reportId" element={<Report />} />
        {/* Add appointment routes */}
        <Route path="/appointments/new" element={<AppointmentDetails />} />
        <Route path="/appointments/:appointmentId" element={<AppointmentDetails />} />
        <Route path="/appointments/:doctorId/pending" element={<PendingAppointments />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

const authScreenLabels = {
	en: {
		'Sign in to your account': 'Doctor Scheduling System',
		'Create account': ' ',
		'No account? ': ' ',
		'Sign In': 'LOGIN'
	}
};
I18n.setLanguage('en');
I18n.putVocabularies(authScreenLabels);

export default function AppWithAuth() {
  return (
    <React.Fragment>
      <Authenticator 
        loginMechanisms={['username']}
        hideSignUp={true}
        formFields={{
          signIn: {
            username: {
              placeholder: 'Username',
              isRequired: true,
              label: 'Username'
            }
          }
        }}
      >
        {({ signOut, user }) => <App />}
      </Authenticator>
    </React.Fragment>
  );
}
